import React from "react";
import { T } from "../../Constants";
import { Dropdown, DropdownToggleProps } from "react-bootstrap";
import { useLanguage } from "../../hooks";

export type ButtonSelectProps<A extends string = string> = {
    /** The different options */
    options: T.Option<Record<"variant", T.ColorTypes> & { icon?: string, displayInDropdown?: boolean }, A>[];
    /** The current selected option */
    value: A;
    /** The default value, when nothing is selected */
    default_value: A;
    /** Callback, when the value change */
    onChange?: (value: A) => void;
    /** The size of the button */
    size?: "sm" | "lg";
    /** Disable the ButtonSelect */
    disabled?: boolean;
    /** Title of the button, displayed instead of the current selection */
    title?: string | React.ReactElement;
}

const RenderButtonSelect = <A extends string = string,>(props: ButtonSelectProps<A>) => {
    const lg = useLanguage();

    const current_option = React.useMemo(() => {
        const value = props.value || props.default_value;
        return props.options?.find(o => o?.value === value);
    }, [props.value, props.options, props.default_value]);

    const toggle = React.useMemo(() => {
        let content = null;
        const toggle_props = { size: props.size, disabled: props.disabled } as DropdownToggleProps;

        if (typeof props.title === "string") content = lg.getStaticText(props.title);
        else if (props.title) content = props.title;
        else if (current_option) {
            toggle_props.variant = current_option?.variant;
            content = (
                <>
                    {current_option.icon && <i className={`fa fa-${current_option.icon} me-1`}></i>}
                    {lg.getStaticElem(current_option?.label)}
                </>
            );
        }
        else return null;

        return <Dropdown.Toggle {...toggle_props}>{content}</Dropdown.Toggle>;
    }, [current_option, lg, props.disabled, props.size, props.title]);

    if (!toggle || !props.options) return null;
    return (
        <Dropdown>
            {toggle}
            <Dropdown.Menu>
                {props.options.map(o => (
                    <Dropdown.Item onClick={() => props.onChange?.(o.value)} key={o.value}>
                        {o.displayInDropdown !== false && o.icon && <i className={`fa fa-${o.icon} me-1`}></i>}
                        {lg.getStaticElem(o.label)}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
};


const ButtonSelect = RenderButtonSelect as <A extends string = string>(props: ButtonSelectProps<A>) => React.ReactElement;

export default ButtonSelect;